import React, { useState, useCallback, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import UserPlatformAPI from "../../user-api"
import {
  UsersIcon,
  TrashIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline"

/**
 * User statistics overview component
 * Displays total, active, disabled, and deleted user counts
 */
const UserStatistics = ({ onRefresh }) => {
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    disabled: 0,
    deleted: 0
  })
  const [loading, setLoading] = useState(true)

  const loadStatistics = useCallback(async () => {
    setLoading(true)
    try {
      const response = await UserPlatformAPI.GetUserStatistics()
      if (response.result) {
        setStats(response.data)
      } else {
        console.error("Failed to load user statistics:", response.error)
      }
    } catch (error) {
      console.error("Failed to load user statistics:", error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadStatistics()
  }, [loadStatistics, onRefresh])

  const StatCard = ({ title, value, icon: Icon, variant = "primary", showPercentage = true }) => {
    const getColorClasses = () => {
      switch (variant) {
        case "success":
          return "bg-admin_success text-white"
        case "warning":
          return "bg-admin_warning text-white"
        case "error":
          return "bg-admin_error text-white"
        default:
          return "bg-admin_primary_lt text-white"
      }
    }

    // Calculate percentage based on total users (excluding deleted for active/disabled)
    const getPercentage = () => {
      if (loading || !showPercentage) return null

      let denominator = stats.total
      if (title === "Deleted Users") {
        // For deleted users, show percentage of all users including deleted
        denominator = stats.total + stats.deleted
      }

      if (denominator === 0) return "0%"

      const percentage = Math.round((value / denominator) * 100)
      return `${percentage}%`
    }

    return (
      <div className="bg-admin_paper border border-admin_bordered rounded-xl p-6 flex items-end justify-between">
        <div className="flex-1">
          <Typography
            variant="admin_txt1_dim"
            className="mb-2"
          >
            {title}
          </Typography>

          <div className="flex items-baseline gap-3">
            <Typography
              component="output"
              variant="admin_h2"
              className="text-admin_text font-bold"
            >
              {loading ? "..." : value.toLocaleString()}
            </Typography>

            {showPercentage && !loading && (
              <Typography variant="admin_txt1_dim">
                {getPercentage()}
              </Typography>
            )}
          </div>
        </div>

        <div className={`p-3 rounded-lg ${getColorClasses()}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <StatCard
        title="Total Users"
        value={stats.total}
        icon={UsersIcon}
        variant="primary"
        showPercentage={false}
      />
      <StatCard
        title="Active Users"
        value={stats.active}
        icon={UserGroupIcon}
        variant="success"
      />
      <StatCard
        title="Disabled Users"
        value={stats.disabled}
        icon={ExclamationTriangleIcon}
        variant="warning"
      />
      <StatCard
        title="Deleted Users"
        value={stats.deleted}
        icon={TrashIcon}
        variant="error"
      />
    </div>
  )
}

export default UserStatistics
